/**
 * Standalone implementation of @optimgov/module-auth.
 *
 * The original package lived in the retired OptimGov SDK feed and delegated
 * SSO to the Hub platform. This local implementation keeps the exact same
 * public API (AuthProvider, useAuth, buildMsalConfig, AuthUser) but talks
 * directly to Microsoft Entra ID via @azure/msal-browser — any Entra tenant
 * configured through ENTRA_* env vars / window.__CONTRACTACIO_CONFIG__ works.
 *
 * Modes:
 *   - "mock": LOCAL_MODE development. Auth core is bypassed — useAuth
 *     (src/hooks/useAuth.ts) derives state from localStorage.local_role.
 *   - "msal": production. Redirect-based Entra login; access tokens for the
 *     configured API scope are attached by the auth bridge (apiUtils).
 *
 * Next to Microsoft, people with an OptimGov password account sign in through
 * our own form (lib/password-session.ts). When such a session is stored, it wins
 * and MSAL is not initialised at all; otherwise the Microsoft path runs as before.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import {
	PublicClientApplication,
	InteractionRequiredAuthError,
	BrowserAuthError,
	type AccountInfo,
	type Configuration,
} from "@azure/msal-browser"
import { clearPasswordSession, readPasswordSession, type PasswordSession } from "@/lib/password-session"

export interface AuthUser {
	userId: string
	email: string | null
	displayName: string | null
}

export interface ModuleAuthValue {
	isAuthenticated: boolean
	user: AuthUser | null
	isLoading: boolean
	/** Which sign-in the current session came from (null when signed out). */
	provider: "microsoft" | "password" | null
	getAccessToken: () => Promise<string>
	/**
	 * Access token for Microsoft Graph (a different resource than our API, so a
	 * separate token). Silent first; if Entra needs interaction the page is sent
	 * through a redirect and this promise never resolves usefully. Empty string
	 * in mock/local mode.
	 */
	getGraphAccessToken: (scopes: string[]) => Promise<string>
	login: () => Promise<void>
	logout: () => Promise<void>
}

// Backwards-compatible alias with the SDK's exported name
export type AuthContextValue = ModuleAuthValue

export interface BuildMsalConfigParams {
	clientId?: string
	tenantId?: string
	/** Legacy Hub URL — accepted for call-site compatibility, unused standalone. */
	hubUrl?: string
	redirectUri?: string
	postLogoutRedirectUri?: string
	authority?: string
	knownAuthorities?: string[]
	apiScopes?: string[]
}

export interface MsalAuthConfig {
	msalConfiguration: Configuration
	apiScopes: { default: string[]; api: string[] }
}

/**
 * Build the MSAL configuration from runtime config / env vars.
 * Defaults target a standard Entra ID tenant; CIAM / External ID tenants can
 * override `authority` + `knownAuthorities` (ENTRA_AUTHORITY env vars).
 */
export function buildMsalConfig(params: BuildMsalConfigParams = {}): MsalAuthConfig {
	const clientId = params.clientId ?? ""
	const authority = params.authority
		|| `https://login.microsoftonline.com/${params.tenantId || "organizations"}`

	let knownAuthorities = (params.knownAuthorities ?? []).filter(Boolean)
	if (knownAuthorities.length === 0) {
		try {
			knownAuthorities = [new URL(authority).hostname]
		} catch {
			knownAuthorities = []
		}
	}

	const apiScopes = (params.apiScopes ?? []).filter(Boolean)
	const api = apiScopes.length > 0 ? apiScopes : (clientId ? [`api://${clientId}/access`] : [])

	const redirectUri = params.redirectUri
		|| (typeof window !== "undefined" ? `${window.location.origin}/` : "/")

	return {
		msalConfiguration: {
			auth: {
				clientId,
				authority,
				knownAuthorities,
				redirectUri,
				postLogoutRedirectUri: params.postLogoutRedirectUri || redirectUri,
			},
			cache: {
				// localStorage keeps the session across tabs and full reloads
				cacheLocation: "localStorage",
			},
		},
		apiScopes: {
			default: ["openid", "profile", "email", ...api],
			api,
		},
	}
}

const MOCK_VALUE: ModuleAuthValue = {
	isAuthenticated: false,
	user: null,
	isLoading: false,
	provider: null,
	getAccessToken: async () => "",
	getGraphAccessToken: async () => "",
	login: async () => {
		console.warn("[module-auth] login() is a no-op in mock mode — use the local role selector")
	},
	logout: async () => {},
}

const AuthContext = createContext<ModuleAuthValue>(MOCK_VALUE)

// Set after a self-heal reload so a persistently broken cache can't loop.
const SELF_HEAL_FLAG = "contractacio.auth.selfHealed"

// Counts acquireTokenRedirect round-trips in this tab. If the authority keeps
// answering "interaction required" after the interaction, redirecting again
// just loops the page forever (perceived as an eternal loading screen) — after
// 2 round-trips we throw instead, so the UI surfaces a visible auth error.
const TOKEN_REDIRECT_COUNT = "contractacio.auth.tokenRedirects"

// Concurrent callers (pollers, first-paint fetches) that all hit an expired
// session share ONE redirect instead of each burning a slot of the guard above.
let pendingTokenRedirect: Promise<void> | null = null

/**
 * Remove every MSAL-related key from web storage: `msal.*` index/metadata keys
 * plus credential entries keyed by the authority host. Used to self-heal a
 * corrupted cache (e.g. tokens minted for a stale redirect origin, or an
 * orphaned interaction_in_progress flag) that otherwise blocks login until the
 * user clears site data manually.
 */
function purgeMsalStorage() {
	for (const storage of [window.localStorage, window.sessionStorage]) {
		for (const key of Object.keys(storage)) {
			if (
				key.startsWith("msal.")
				|| key.includes("login.microsoftonline.com")
				|| key.includes("login.windows.net")
				|| key.includes("ciamlogin.com")
			) {
				storage.removeItem(key)
			}
		}
	}
}

/**
 * The one-click equivalent of "clear site data and come back": drop every MSAL
 * credential plus our own redirect/self-heal counters, then reload at the root
 * so the next boot starts from the login form. Used by the session-recovery
 * screen when token acquisition keeps failing.
 */
export function resetAuthSession() {
	purgeMsalStorage()
	clearPasswordSession()
	for (const key of Object.keys(window.sessionStorage)) {
		if (key.startsWith("contractacio.auth.")) window.sessionStorage.removeItem(key)
	}
	window.location.replace("/")
}

export interface AuthProviderProps {
	mode?: "mock" | "msal"
	/** Legacy hub/module distinction — accepted for compatibility, unused standalone. */
	context?: "hub" | "module"
	msalConfig?: MsalAuthConfig
	children?: React.ReactNode
}

function mapAccount(account: AccountInfo): AuthUser {
	return {
		userId: account.localAccountId,
		email: account.username || null,
		displayName: account.name ?? null,
	}
}

function MsalAuthProvider({ config, children }: { config: MsalAuthConfig; children?: React.ReactNode }) {
	const pcaRef = useRef<PublicClientApplication | null>(null)
	if (!pcaRef.current) {
		pcaRef.current = new PublicClientApplication(config.msalConfiguration)
	}
	const pca = pcaRef.current

	const [isLoading, setIsLoading] = useState(true)
	const [account, setAccount] = useState<AccountInfo | null>(null)

	useEffect(() => {
		let cancelled = false

		;(async () => {
			await pca.initialize()
			// Completes a pending redirect flow (returns null on a plain load)
			const redirectResult = await pca.handleRedirectPromise()
			if (redirectResult?.account) {
				pca.setActiveAccount(redirectResult.account)
			} else {
				// A plain load (user reload, new navigation) is not part of a
				// redirect loop: give this tab fresh interactive attempts. The
				// counter is sessionStorage, so without this a tripped guard
				// survived every reload and only clearing site data got out.
				window.sessionStorage.removeItem(TOKEN_REDIRECT_COUNT)
			}
			const active = pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? null
			if (active) pca.setActiveAccount(active)
			if (!cancelled) {
				setAccount(active)
				setIsLoading(false)
			}
		})().catch((err) => {
			console.error("[module-auth] MSAL initialization failed:", err)
			// Self-heal: purge the MSAL cache and reload once. The flag lives in
			// sessionStorage (survives the reload, not the tab) and is set AFTER
			// the purge so the purge can't delete it.
			if (!window.sessionStorage.getItem(SELF_HEAL_FLAG)) {
				purgeMsalStorage()
				window.sessionStorage.setItem(SELF_HEAL_FLAG, "1")
				window.location.reload()
				return
			}
			if (!cancelled) {
				setAccount(null)
				setIsLoading(false)
			}
		})

		return () => {
			cancelled = true
		}
	}, [pca])

	const login = useCallback(async () => {
		// prompt=select_account: without it, a live CIAM SSO cookie silently
		// re-issues tokens for the previous account, so there is no way to sign
		// in with a different one. The explicit button click always shows the
		// account picker (with "Use another account").
		const request = { scopes: config.apiScopes.default, prompt: "select_account" }
		try {
			await pca.loginRedirect(request)
		} catch (err) {
			// A stuck interaction_in_progress flag rejects loginRedirect forever —
			// purge the stale state and retry once (user-initiated, so no loop risk)
			console.warn("[module-auth] loginRedirect failed — purging MSAL cache and retrying:", err)
			purgeMsalStorage()
			await pca.loginRedirect(request)
		}
	}, [pca, config])

	const logout = useCallback(async () => {
		const active = pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? undefined
		// id_token_hint tells CIAM WHICH session to end: without it the
		// end_session endpoint shows a "Pick an account" page and, if the user
		// doesn't click, the SSO cookie survives and the next login is silent.
		// Same rule as the server-side /auth/reset: only pass well-formed JWTs.
		const idTokenHint = active?.idToken && active.idToken.split(".").length === 3 ? active.idToken : undefined
		const logoutHint = typeof active?.idTokenClaims?.login_hint === "string" ? active.idTokenClaims.login_hint : undefined
		await pca.logoutRedirect({ account: active, idTokenHint, logoutHint })
	}, [pca])

	const getAccessToken = useCallback(async (): Promise<string> => {
		const active = pca.getActiveAccount() ?? pca.getAllAccounts()[0]
		if (!active) throw new Error("[module-auth] getAccessToken called without an authenticated account")
		try {
			const result = await pca.acquireTokenSilent({ scopes: config.apiScopes.api, account: active })
			window.sessionStorage.removeItem(TOKEN_REDIRECT_COUNT)
			return result.accessToken
		} catch (err) {
			// InteractionRequiredAuthError: expired session / consent needed.
			// BrowserAuthError block_iframe_reload / timed_out: the
			// hidden-iframe renewal broke (blocked third-party cookies, or a stale
			// bundle booting the app inside the iframe) — silent renewal is equally
			// impossible, so both cases fall back to an interactive redirect.
			const isBrokenIframeRenewal = err instanceof BrowserAuthError
				&& (err.errorCode === "block_iframe_reload" || err.errorCode === "timed_out")
			if (err instanceof InteractionRequiredAuthError || isBrokenIframeRenewal) {
				if (!pendingTokenRedirect) {
					const redirects = Number(window.sessionStorage.getItem(TOKEN_REDIRECT_COUNT) || "0")
					if (redirects >= 2) throw err
					window.sessionStorage.setItem(TOKEN_REDIRECT_COUNT, String(redirects + 1))
					// Silent renewal impossible — restart interactively
					pendingTokenRedirect = pca
						.acquireTokenRedirect({ scopes: config.apiScopes.api, account: active })
						.finally(() => { pendingTokenRedirect = null })
				}
				await pendingTokenRedirect
				return ""
			}
			throw err
		}
	}, [pca, config])

	const getGraphAccessToken = useCallback(async (scopes: string[]): Promise<string> => {
		const active = pca.getActiveAccount() ?? pca.getAllAccounts()[0]
		if (!active) throw new Error("[module-auth] getGraphAccessToken called without an authenticated account")
		try {
			// One resource per request (MSAL rule): Graph scopes never ride along the API scope.
			const result = await pca.acquireTokenSilent({ scopes, account: active })
			return result.accessToken
		} catch (err) {
			// Consent is tenant-wide (admin), so this only happens when the session
			// itself needs renewing. User-initiated action, no loop guard needed.
			if (err instanceof InteractionRequiredAuthError) {
				await pca.acquireTokenRedirect({ scopes, account: active })
				return ""
			}
			throw err
		}
	}, [pca])

	const value = useMemo<ModuleAuthValue>(() => ({
		isAuthenticated: !!account,
		user: account ? mapAccount(account) : null,
		isLoading,
		provider: account ? "microsoft" : null,
		getAccessToken,
		getGraphAccessToken,
		login,
		logout,
	}), [account, isLoading, getAccessToken, getGraphAccessToken, login, logout])

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

function PasswordAuthProvider({ session, children }: { session: PasswordSession; children?: React.ReactNode }) {
	const value = useMemo<ModuleAuthValue>(() => ({
		isAuthenticated: true,
		user: { userId: `pwd:${session.email}`, email: session.email, displayName: session.name || null },
		isLoading: false,
		provider: "password",
		getAccessToken: async () => {
			// Re-read on every call: a password change in this tab stores a new token,
			// and an expired session must send the person back to the sign-in form.
			const current = readPasswordSession()
			if (current) return current.token
			window.location.reload()
			return new Promise<string>(() => {})
		},
		getGraphAccessToken: async () => "",
		login: async () => {},
		logout: async () => {
			clearPasswordSession()
			window.location.replace("/login")
		},
	}), [session])
	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function AuthProvider({ mode = "msal", msalConfig, children }: AuthProviderProps) {
	// Read once per mount: signing in or out with a password reloads the page.
	const [passwordSession] = useState(() => (mode === "msal" ? readPasswordSession() : null))
	if (passwordSession) {
		return <PasswordAuthProvider session={passwordSession}>{children}</PasswordAuthProvider>
	}
	if (mode === "msal" && msalConfig?.msalConfiguration.auth.clientId) {
		return <MsalAuthProvider config={msalConfig}>{children}</MsalAuthProvider>
	}
	// Mock mode (LOCAL_MODE) or missing MSAL config — inert provider
	return <AuthContext.Provider value={MOCK_VALUE}>{children}</AuthContext.Provider>
}

export function useAuth(): ModuleAuthValue {
	return useContext(AuthContext)
}
